import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { Reveal } from '@/components/landing/motion';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

/**
 * No-signup demo. Everything is seeded client-side from a static fixture
 * so the page works without any backend dependency.
 * The point is to prove the *feel* of the product in under 30 seconds.
 */

interface Turn {
  question: string;
  answer: { text: string; chunkIds: string[] };
  model: string;
  cost: string;
}

const SAMPLE_TURNS: Turn[] = [
  {
    question: 'Project Atlasの最新の議事録から、現状のリスクを3つ要約して。',
    answer: {
      text: '直近のProject Atlas議事録で繰り返し言及されたリスクは次の3点です: \n\n(1) ベンダーA社のAPIレート制限による遅延 [chunk:1242] — 4月以降毎週2-3回 timeout が発生。\n(2) インドのチームの採用遅れ [chunk:1287] — 当初Q2の3名採用に対し1名のみ着地。\n(3) UI再設計に伴うE2Eテスト不足 [chunk:1302] — テストカバレッジ73%。',
      chunkIds: ['1242', '1287', '1302'],
    },
    model: 'Knowlez AI · reasoning',
    cost: '¥0.42',
  },
  {
    question: 'What are the open compliance gaps for J-SOX scope in FY2026 Q1?',
    answer: {
      text: 'Two open gaps surfaced in the FY2026 Q1 J-SOX review:\n\n1. Segregation-of-duties on the procurement approval flow has not been re-attested since March 2026 [chunk:2104]. Latest evidence is the Feb 2026 attestation.\n2. Access-revocation timing for terminated employees averages 4.2 business days against the policy 1-day SLA [chunk:2118]. The policy text is unchanged [chunk:2101].',
      chunkIds: ['2104', '2118', '2101'],
    },
    model: 'Knowlez AI · fast',
    cost: '¥0.18',
  },
];

export function DemoPage() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(0);
  useDocumentMeta({
    title: 'Live demo — Knowlez',
    description: 'No-signup Knowlez demo: ask questions across mixed JP + global documents, see cited chunks, model + cost per turn.',
    path: '/demo',
  });
  return (
    <div className="min-h-screen paper-archive">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex text-[10px] uppercase tracking-wide font-semibold text-indigo-700 bg-indigo-50 rounded-md px-2 py-0.5 mb-2">
              Demo · seeded data · no signup
            </div>
            <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">
              {t('demo.title', { defaultValue: 'Acme K.K. knowledge workspace' })}
            </h1>
            <p className="mt-1 text-ink-600">
              {t('demo.sub', {
                defaultValue:
                  'Real Knowlez UI rendering against seeded conversations. Click a chip to reveal its underlying chunk.',
              })}
            </p>
          </div>
          <Link to="/signup" className="btn-press inline-flex items-center px-5 py-3">
            {t('hero.primary_cta', { defaultValue: 'Start free' })}
          </Link>
        </div>

        {SAMPLE_TURNS.map((turn, i) => (
          <Reveal key={i}>
          <div className="card-paper p-5">
            <div className="rounded-xl bg-accent-500 text-white px-3 py-2 text-sm mb-3 max-w-2xl">
              {turn.question}
            </div>
            <div className="rounded-xl border border-ink-200 px-4 py-3 text-sm leading-relaxed">
              <CitedMarkdown content={turn.answer.text} />
            </div>
            <div className="mt-2 text-[10px] font-mono text-ink-400 flex items-center justify-between">
              <span>
                {turn.model} · {turn.cost}
              </span>
              <button onClick={() => setOpen((c) => (c === i ? null : i))} className="hover:underline text-indigo-600">
                {open === i ? 'Hide retrieval trace' : 'Show retrieval trace'}
              </button>
            </div>
            {open === i && (
              <div className="mt-3 rounded-xl bg-ink-50 border border-ink-200 p-4 text-xs">
                <div className="text-ink-500 mb-2 font-mono">
                  hybrid_retrieve · top_k=8 · lanes=dense,lexical · rrf k=60
                </div>
                <ul className="space-y-1 font-mono">
                  {turn.answer.chunkIds.map((id) => (
                    <li key={id} className="text-ink-700">
                      <span className="text-orange-600">chunk:{id}</span> · doc=meeting_minutes_2026Q1 · page=3 · score=0.78
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          </Reveal>
        ))}

        <div className="rounded-2xl bg-accent-600 text-white px-6 py-8 text-center">
          <h3 className="display text-2xl font-semibold tracking-tight">
            {t('cta.headline', { defaultValue: 'See it on your own corpus.' })}
          </h3>
          <p className="mt-1 text-indigo-100">
            {t('hero.no_card', { defaultValue: 'No credit card. 10 free seats forever.' })}
          </p>
          <Link
            to="/signup"
            className="mt-4 inline-flex items-center px-5 py-2.5 rounded-lg bg-white text-accent-700 text-sm font-semibold hover:bg-ink-100 transition"
          >
            {t('nav.signup', { defaultValue: 'Sign up' })}
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

